import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { DebugMaterialRegistry } from '../debug/debug-materials'
import type { WthitInspector } from '../debug/wthit-inspector'
import type { RAPIER } from '../physics'
import { createDebugToast } from '../debug/_debug-helpers'
import propManifest from '../../assets/models/all_props_manifest.json'

/** Keyed by prop id for O(1) look-up of canonical pedestalFitScale */
const MANIFEST_MAP = new Map(
  (propManifest as Array<{ id: string; pedestalFitScale?: number; file?: string }>)
    .map((p) => [p.id, p]),
)

export interface HotReloadResult {
  success: boolean
  propId: string
  message: string
  triangleCount?: number
}

const showReloadToast = createDebugToast({
  id: 'gltf-reload-toast',
  topPx: 118,
  successColor: 'rgba(0, 255, 136, 0.95)',
  successShadowRgba: 'rgba(0, 255, 136, 0.4)',
})

export class GltfHotReloader {
  private static gltfLoader = new GLTFLoader()

  /**
   * Reloads a specific glTF prop model in-place in the active Three.js scene
   */
  static async reloadPropInPlace(
    propId: string,
    scene: THREE.Scene,
    debugRegistry?: DebugMaterialRegistry,
    world?: RAPIER.World,
  ): Promise<HotReloadResult> {
    const cleanId = propId.replace(/\.glb$/i, '')
    const url = `/assets/models/props/${encodeURIComponent(cleanId)}.glb?t=${Date.now()}`

    // 1. Find existing mesh(es) in the scene matching this prop ID
    const matchingMeshes: THREE.Mesh[] = []
    scene.traverse((node) => {
      if (node instanceof THREE.Mesh) {
        if (
          node.name === cleanId ||
          node.name === propId ||
          (node.userData && (node.userData.id === cleanId || node.userData.propId === cleanId))
        ) {
          matchingMeshes.push(node)
        }
      }
    })

    if (matchingMeshes.length === 0) {
      const msg = `❌ No active scene mesh found matching '${cleanId}'`
      showReloadToast(msg, false)
      console.warn(`[HOT-RELOAD] ${msg}`)
      return { success: false, propId: cleanId, message: msg }
    }

    // 2. Fetch fresh glTF asset from server with cache-busting
    let loadedGltf
    try {
      loadedGltf = await this.gltfLoader.loadAsync(url)
    } catch (err: any) {
      const msg = `❌ Failed to fetch glTF '${url}': ${err?.message || err}`
      showReloadToast(msg, false)
      console.error(`[HOT-RELOAD] ${msg}`)
      return { success: false, propId: cleanId, message: msg }
    }

    // 3. Extract fresh geometry from loaded glTF scene
    let freshMesh: THREE.Mesh | null = null
    loadedGltf.scene.traverse((node: THREE.Object3D) => {
      if (!freshMesh && node instanceof THREE.Mesh) {
        freshMesh = node
      }
    })

    if (!freshMesh) {
      const msg = `❌ glTF '${url}' contains no valid geometry`
      showReloadToast(msg, false)
      return { success: false, propId: cleanId, message: msg }
    }

    const validMesh = freshMesh as THREE.Mesh
    if (!validMesh.geometry) {
      const msg = `❌ glTF '${url}' contains no valid geometry`
      showReloadToast(msg, false)
      return { success: false, propId: cleanId, message: msg }
    }

    const newGeometry = validMesh.geometry.clone()
    newGeometry.computeVertexNormals()
    newGeometry.computeBoundingBox()
    newGeometry.computeBoundingSphere()

    let totalTris = 0
    if (newGeometry.index) {
      totalTris = newGeometry.index.count / 3
    } else if (newGeometry.attributes.position) {
      totalTris = newGeometry.attributes.position.count / 3
    }

    // 4. Hot-swap geometry in place on all matching scene meshes
    for (const mesh of matchingMeshes) {
      const meshGeometry = newGeometry.clone()

      // Scale from manifest — the single source of truth, independent of runtime state
      const manifestEntry = MANIFEST_MAP.get(cleanId)
      const fitScale = manifestEntry?.pedestalFitScale ?? 1.0

      if (fitScale !== 1.0) {
        meshGeometry.scale(fitScale, fitScale, fitScale)
      }
      meshGeometry.computeBoundingBox()

      const oldGeom = mesh.geometry
      mesh.geometry = meshGeometry
      if (oldGeom) oldGeom.dispose()

      // Position is unchanged: applying the same manifest pedestalFitScale produces
      // an identical bbox.min.y, so the existing mesh.position.y is already correct.

      // Visual pulse flash feedback (cyan outline wireframe)
      if (typeof requestAnimationFrame !== 'undefined') {
        const flashMat = new THREE.MeshBasicMaterial({
          color: 0x00ffff,
          wireframe: true,
          transparent: true,
          opacity: 1.0,
        })
        const flashMesh = new THREE.Mesh(mesh.geometry, flashMat)
        flashMesh.position.copy(mesh.position)
        flashMesh.rotation.copy(mesh.rotation)
        flashMesh.scale.copy(mesh.scale)
        scene.add(flashMesh)

        const startTime = performance.now()
        const animateFlash = () => {
          const elapsed = performance.now() - startTime
          if (elapsed > 400) {
            scene.remove(flashMesh)
            flashMat.dispose()
          } else {
            flashMat.opacity = 1.0 - elapsed / 400
            requestAnimationFrame(animateFlash)
          }
        }
        requestAnimationFrame(animateFlash)
      }
    }

    // 5. Update debug registry if present
    if (debugRegistry) {
      for (const mesh of matchingMeshes) {
        debugRegistry.updateMeshGeometry(mesh)
      }
    }

    // 6. Physics lifecycle hook (world reference passed)
    if (world) {
      // Future: hot-reconstruct physics colliders if dynamic streaming needs in-place resize
    }

    const msg = `⚡ HOT-RELOADED: ${cleanId} (${totalTris} tris)`
    showReloadToast(msg, true)
    console.log(`%c[HOT-RELOAD] ${msg}`, 'color: #00ff88; font-weight: bold;')

    return {
      success: true,
      propId: cleanId,
      message: msg,
      triangleCount: totalTris,
    }
  }

  /**
   * Reloads the currently targeted prop under the player crosshairs
   */
  static async reloadTargetedProp(
    wthitInspector: WthitInspector,
    scene: THREE.Scene,
    debugRegistry?: DebugMaterialRegistry,
    world?: RAPIER.World,
  ): Promise<HotReloadResult> {
    const hitData = wthitInspector.getCurrentTargetData()
    if (!hitData || !hitData.id) {
      const msg = '⚠️ No prop currently targeted under crosshairs'
      showReloadToast(msg, false)
      return { success: false, propId: 'none', message: msg }
    }
    return this.reloadPropInPlace(hitData.id, scene, debugRegistry, world)
  }

  /**
   * Reloads all props in the active scene
   */
  static async reloadAllProps(
    scene: THREE.Scene,
    debugRegistry?: DebugMaterialRegistry,
    world?: RAPIER.World,
  ): Promise<{ reloadedCount: number; results: HotReloadResult[] }> {
    // Only reload mesh names that are known manifest prop IDs — never wall/floor/pedestal geometry
    const propIds = new Set<string>()
    scene.traverse((node) => {
      if (node instanceof THREE.Mesh && node.name && MANIFEST_MAP.has(node.name)) {
        propIds.add(node.name)
      }
    })

    const results: HotReloadResult[] = []
    for (const id of propIds) {
      const res = await this.reloadPropInPlace(id, scene, debugRegistry, world)
      if (res.success) {
        results.push(res)
      }
    }

    showReloadToast(`⚡ RELOADED ${results.length} GLTF PROPS`, true)
    return {
      reloadedCount: results.length,
      results,
    }
  }
}
